import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import {
  OW_BOOK_A_DEMO,
  OW_BUSINESS_OVERVIEW,
  OW_ORBIT,
  OW_ROOT,
  OW_SCHEDULE_RENEWAL_REVIEW,
  OW_SYSTEM_SUPPORT
} from 'containers/Setting/components/RoleFormModal/overwatch';
import { useTypedSelector } from 'hooks';

/** FR section id to its permission key (features/PLA-9674/roles/api-contract.md). */
export const OW_SECTION_KEYS = {
  campaignPerformance: `${OW_BUSINESS_OVERVIEW}.campaign_performance`,
  engagementSummary: `${OW_BUSINESS_OVERVIEW}.engagement_summary`,
  campaignAttention: `${OW_BUSINESS_OVERVIEW}.campaign_attention`,
  executionStatus: `${OW_BUSINESS_OVERVIEW}.execution_status`,
  performanceUserGrowth: `${OW_BUSINESS_OVERVIEW}.performance_and_user_growth`,
  anomalies: `${OW_BUSINESS_OVERVIEW}.detected_anomalies_and_required_actions`,
  operationalVisibility: `${OW_BUSINESS_OVERVIEW}.operational_visibility`,
  commercialIntelligence: `${OW_BUSINESS_OVERVIEW}.executive_commercial_intelligence`,
  contractSummary: `${OW_BUSINESS_OVERVIEW}.contract_summary_and_commercial_terms`,
  capabilities: `${OW_BUSINESS_OVERVIEW}.your_perx_capabilities`,
  exploreMore: `${OW_BUSINESS_OVERVIEW}.explore_more_with_perx`,
  whatsNew: `${OW_BUSINESS_OVERVIEW}.whats_new_in_perx`,
  weeklyUpdates: `${OW_BUSINESS_OVERVIEW}.weekly_updates`,
  systemHealth: `${OW_SYSTEM_SUPPORT}.system_health_overview`,
  slaPlan: `${OW_SYSTEM_SUPPORT}.sla_plan_details`,
  incidentBoard: `${OW_SYSTEM_SUPPORT}.incident_and_maintenance_board`,
  supportTicketCreation: `${OW_SYSTEM_SUPPORT}.create_support_ticket`,
  activeTickets: `${OW_SYSTEM_SUPPORT}.active_support_tickets`,
  knowledgeBase: `${OW_SYSTEM_SUPPORT}.enterprise_knowledge_base_and_playbooks`,
  securityDocuments: `${OW_SYSTEM_SUPPORT}.security_and_audit_compliance_documents`,
  engineVersions: `${OW_SYSTEM_SUPPORT}.approved_platform_technology_and_engine_versions`,
  auditLog: `${OW_SYSTEM_SUPPORT}.audit_log`
} as const;

export type OverwatchSection = keyof typeof OW_SECTION_KEYS;

export interface IOverwatchAccess {
  businessOverview: boolean;
  systemSupport: boolean;
  sections: Record<OverwatchSection, boolean>;
  canUseOrbit: boolean;
  canBookDemo: boolean;
  canScheduleRenewalReview: boolean;
  /** BO-06's CTA: Detected Anomalies, System & Support and Create Support Ticket together. */
  canCreateTicketFromAnomaly: boolean;
}

interface IAbilityPermission {
  resource_name: string;
  actions?: string[];
}

const screenOf = (key: string) => key.split('.').slice(0, 2).join('.');

/**
 * What the signed-in role may see on Perx Overwatch, read from the same `state.ability` slice the
 * route's `Ability` gate uses. Every grant also needs each ancestor's `view`, so a stale child grant
 * under a revoked parent opens nothing. PLA-9674.
 */
const useOverwatchAccess = (): IOverwatchAccess => {
  const permissions: IAbilityPermission[] | undefined = useTypedSelector(
    state => state.ability?.sections?.permissions,
    shallowEqual
  );

  return useMemo(() => {
    const has = (key: string) =>
      (permissions ?? []).some(
        permission => permission.resource_name === key && permission.actions?.includes('view')
      );

    const root = has(OW_ROOT);
    const screenGranted = (screen: string) => root && has(screen);
    const sectionGranted = (key: string) => screenGranted(screenOf(key)) && has(key);

    const sections = Object.fromEntries(
      Object.entries(OW_SECTION_KEYS).map(([section, key]) => [section, sectionGranted(key)])
    ) as Record<OverwatchSection, boolean>;

    return {
      businessOverview: screenGranted(OW_BUSINESS_OVERVIEW),
      systemSupport: screenGranted(OW_SYSTEM_SUPPORT),
      sections,
      canUseOrbit: root && has(OW_ORBIT),
      canBookDemo: sections.exploreMore && has(OW_BOOK_A_DEMO),
      canScheduleRenewalReview: sections.contractSummary && has(OW_SCHEDULE_RENEWAL_REVIEW),
      canCreateTicketFromAnomaly: sections.anomalies && sections.supportTicketCreation
    };
  }, [permissions]);
};

export default useOverwatchAccess;
